import { FormEvent, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ArrowRight as NavArrowIcon } from '@phosphor-icons/react/dist/ssr/ArrowRight'
import { ArrowUpRight as ExternalLinkIcon } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { BookOpen as LearnIcon } from '@phosphor-icons/react/dist/ssr/BookOpen'
import { ClockCounterClockwise as HistoryIcon } from '@phosphor-icons/react/dist/ssr/ClockCounterClockwise'
import { Cube as CabinetIcon } from '@phosphor-icons/react/dist/ssr/Cube'
import { Lightning as LightningIcon } from '@phosphor-icons/react/dist/ssr/Lightning'
import { MagnifyingGlass as SearchIcon } from '@phosphor-icons/react/dist/ssr/MagnifyingGlass'
import { Receipt as TransactionIcon } from '@phosphor-icons/react/dist/ssr/Receipt'
import { ShoppingBagOpen as ProductIcon } from '@phosphor-icons/react/dist/ssr/ShoppingBagOpen'
import { Sparkle as SparkleIcon } from '@phosphor-icons/react/dist/ssr/Sparkle'
import { Storefront as StoreIcon } from '@phosphor-icons/react/dist/ssr/Storefront'
import { Tag as PromotionIcon } from '@phosphor-icons/react/dist/ssr/Tag'
import { Video as MarketingIcon } from '@phosphor-icons/react/dist/ssr/Video'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'
import debounce from 'lodash/debounce'
import { useNavigate } from 'react-router-dom'

import {
	addRecentItem,
	buildSearchResultHref,
	clearRecentItems,
	deleteRecentItem,
	enrichRecentItems,
	getRecentItems,
	searchAssistant,
	type AssistantSearchResult,
	type RecentItem,
} from '@/lib/assistant-api'
import { track } from '@/lib/segment'
import { useChat } from '@/contexts/chat-context'
import { useTypeformNav } from '@/contexts/typeform-nav-context'
import { useUser } from '@/hooks/use-user'
import { CloudinaryImageThumbnail } from '@/components/core/thumbnail'

import { canAccessOmnibarHref, filterCommandItemsForUser } from './omnibar-access'
import { getOmnibarEntityColor } from './omnibar-colors'
import { ALL_STATIC_ITEMS, QUICK_ACTIONS, scoreCommandItem, type CommandItem } from './omnibar-items'

type ResultIcon = 'transaction' | 'store' | 'cabinet' | 'product' | 'promotion' | 'marketing' | 'learn'

const OMNIBAR_THUMB_PX = 40

interface SearchResultItem {
	key: string
	title: string
	subtitle?: string
	href?: string
	icon: ResultIcon
	imageUrl?: string
	resultType: string
	resultId?: string | number
	external?: boolean
	action: () => void
}

interface SearchResultSection {
	label: string
	items: SearchResultItem[]
}

interface ActionableItem {
	key: string
	label: string
	section: string
	action: () => void
}

function ResultIconDisplay({ icon }: { icon: string }) {
	switch (icon) {
		case 'store':
			return <StoreIcon size={18} />
		case 'product':
			return <ProductIcon size={18} />
		case 'transaction':
			return <TransactionIcon size={18} />
		case 'cabinet':
			return <CabinetIcon size={18} />
		case 'promotion':
			return <PromotionIcon size={18} />
		case 'marketing':
			return <MarketingIcon size={18} />
		case 'learn':
			return <LearnIcon size={18} />
		case 'navigate':
			return <HistoryIcon size={18} />
		default:
			return <HistoryIcon size={18} />
	}
}

function OmnibarResultThumbnail({
	photo,
	icon,
	showIconBadge = false,
}: {
	photo?: string
	icon: string
	showIconBadge?: boolean
}) {
	if (photo) {
		return (
			<Box
				sx={{
					borderRadius: 1,
					flexShrink: 0,
					height: OMNIBAR_THUMB_PX,
					overflow: 'hidden',
					width: OMNIBAR_THUMB_PX,
				}}
			>
				<CloudinaryImageThumbnail photo={photo} size={OMNIBAR_THUMB_PX} pad={icon === 'product'} />
			</Box>
		)
	}

	const accentColor = getOmnibarEntityColor(icon)
	const iconNode = <ResultIconDisplay icon={icon} />

	if (showIconBadge) {
		return (
			<QuickActionIconBadge accentColor={accentColor} size={OMNIBAR_THUMB_PX}>
				{iconNode}
			</QuickActionIconBadge>
		)
	}

	return <OmnibarIconSlot sx={{ color: accentColor, '& svg': { color: 'inherit' } }}>{iconNode}</OmnibarIconSlot>
}

function QuickActionIconBadge({
	accentColor,
	children,
	size,
}: {
	accentColor: string
	children: ReactNode
	size: number
}) {
	return (
		<Box
			sx={{
				alignItems: 'center',
				bgcolor: `${accentColor}1f`,
				borderRadius: 1,
				color: accentColor,
				display: 'flex',
				flexShrink: 0,
				height: size,
				justifyContent: 'center',
				width: size,
				'& svg': { color: 'inherit' },
			}}
		>
			{children}
		</Box>
	)
}

function OmnibarIconSlot({ children, sx }: { children: ReactNode; sx?: object }) {
	return (
		<Box
			sx={{
				alignItems: 'center',
				display: 'flex',
				flexShrink: 0,
				height: OMNIBAR_THUMB_PX,
				justifyContent: 'center',
				width: OMNIBAR_THUMB_PX,
				...sx,
			}}
		>
			{children}
		</Box>
	)
}

function SectionHeader({
	children,
	first,
	icon,
	alignWithListItems,
	action,
}: {
	children: ReactNode
	first?: boolean
	icon?: ReactNode
	alignWithListItems?: boolean
	action?: ReactNode
}) {
	return (
		<ListSubheader
			sx={{
				alignItems: 'center',
				bgcolor: 'background.paper',
				display: 'flex',
				gap: 0.75,
				lineHeight: '28px',
				pl: alignWithListItems ? 3 : 1,
				pr: 0,
				pt: first ? 2 : 3,
				pb: 1,
			}}
		>
			{icon}
			<Typography color="text.secondary" component="span" variant="caption" fontWeight={500}>
				{children}
			</Typography>
			{action ? <Box sx={{ ml: 'auto' }}>{action}</Box> : null}
		</ListSubheader>
	)
}

function formatRecentType(type: string): string {
	switch (type) {
		case 'product':
			return 'Product'
		case 'store':
			return 'Store'
		case 'cabinet':
			return 'Cabinet'
		case 'transaction':
			return 'Transaction'
		case 'promotion':
			return 'Promotion'
		case 'marketing':
			return 'Marketing'
		case 'navigate':
			return 'Page'
		default:
			return type.charAt(0).toUpperCase() + type.slice(1)
	}
}

function QuickActionButton({ item, selected, onClick }: { item: CommandItem; selected: boolean; onClick: () => void }) {
	const accentColor = item.accentColor ?? '#64748b'

	return (
		<Box
			component="button"
			data-nav-index
			data-test={`quick-action-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
			onClick={onClick}
			type="button"
			sx={{
				alignItems: 'center',
				bgcolor: selected ? 'action.hover' : 'background.paper',
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1.5,
				cursor: 'pointer',
				display: 'flex',
				gap: 1.25,
				p: 1.25,
				textAlign: 'left',
				transition: 'background-color 150ms ease-in-out',
				width: '100%',
				'&:hover': {
					bgcolor: 'action.hover',
				},
			}}
		>
			<QuickActionIconBadge accentColor={accentColor} size={32}>
				{item.icon}
			</QuickActionIconBadge>
			<Typography fontWeight={500} sx={{ color: 'text.primary', lineHeight: 1.3 }} variant="body2">
				{item.label}
			</Typography>
		</Box>
	)
}

const ITEM_SX = {
	borderRadius: 1,
	mx: 1,
	my: 0.25,
	px: 2,
	py: 1.25,
	'& .OmnibarItem-arrow': {
		opacity: 0,
		transition: 'opacity 150ms ease-in-out',
	},
	'&:hover .OmnibarItem-arrow, &.Mui-selected .OmnibarItem-arrow': {
		opacity: 1,
	},
	'&.Mui-selected': {
		bgcolor: 'action.hover',
	},
	'&.Mui-selected:hover': {
		bgcolor: 'action.hover',
	},
	'&:hover': {
		bgcolor: 'action.hover',
	},
} as const

const EMPTY_STATE_ROW_SX = {
	...ITEM_SX,
	borderRadius: 1.5,
	mx: 0,
	px: 1.25,
	py: 1.5,
} as const

function ItemArrow({ external = false }: { external?: boolean }) {
	return (
		<Box className="OmnibarItem-arrow" sx={{ alignItems: 'center', display: 'inline-flex', flexShrink: 0 }}>
			{external ? (
				<ExternalLinkIcon color="var(--mui-palette-text-primary)" />
			) : (
				<NavArrowIcon color="var(--mui-palette-text-primary)" />
			)}
		</Box>
	)
}

const QUESTION_PATTERNS =
	/^(how|what|why|when|where|who|which|show|tell|list|compare|analyze|summarize|explain|is there|are there|do i|can you|give me)\b/i

function getSearchShortcutHint(): string {
	if (typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)) {
		return '⌘K'
	}
	return 'Ctrl K'
}

function looksLikeQuestion(query: string): boolean {
	const q = query.trim()
	if (QUESTION_PATTERNS.test(q)) return true
	if (q.endsWith('?')) return true
	return false
}

export function Omnibar() {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const navigate = useNavigate()
	const { user } = useUser()
	const { openTypeform } = useTypeformNav()
	const { startNewChat, hasAccess: hasAiAccess } = useChat()
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [isSearching, setIsSearching] = useState(false)
	const [searchError, setSearchError] = useState<string | null>(null)
	const [results, setResults] = useState<AssistantSearchResult | null>(null)
	const [recentItems, setRecentItems] = useState<RecentItem[]>([])
	const [isRecentItemsLoading, setIsRecentItemsLoading] = useState(false)
	const [activeIndex, setActiveIndex] = useState(-1)
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)
	const queryRef = useRef(query)
	const enrichTokenRef = useRef(0)

	const handleOpen = useCallback(() => {
		setOpen(true)
		setQuery('')
		setResults(null)
		setActiveIndex(-1)
		void track('QuickFindOpened')
	}, [])

	const handleClose = useCallback(() => {
		setOpen(false)
		setQuery('')
		setResults(null)
		setActiveIndex(-1)
	}, [])

	const handleAskAi = useCallback(() => {
		const q = query.trim()
		handleClose()
		if (q) void startNewChat(q)
	}, [query, handleClose, startNewChat])

	// Focus input after dialog opens
	useEffect(() => {
		if (open) {
			const timer = setTimeout(() => {
				inputRef.current?.focus()
			}, 50)
			return () => clearTimeout(timer)
		}
	}, [open])

	// Scroll active item into view
	useEffect(() => {
		if (activeIndex < 0 || !listRef.current) return
		const items = listRef.current.querySelectorAll('[data-nav-index]')
		const activeEl = items[activeIndex] as HTMLElement | undefined
		activeEl?.scrollIntoView({ block: 'nearest' })
	}, [activeIndex])

	const loadRecentItems = useCallback(async () => {
		const token = ++enrichTokenRef.current
		const items = getRecentItems().filter((item) => canAccessOmnibarHref(user, item.href))
		setRecentItems(items)
		if (items.length === 0) {
			setIsRecentItemsLoading(false)
			return
		}
		setIsRecentItemsLoading(true)
		try {
			const enriched = await enrichRecentItems(items)
			if (enrichTokenRef.current !== token) return
			setRecentItems(enriched.filter((item) => canAccessOmnibarHref(user, item.href)))
		} finally {
			if (enrichTokenRef.current === token) setIsRecentItemsLoading(false)
		}
	}, [user])

	const handleClearHistory = useCallback(() => {
		if (isRecentItemsLoading) return
		enrichTokenRef.current++
		clearRecentItems()
		setRecentItems([])
		setActiveIndex(-1)
	}, [isRecentItemsLoading])

	const runSearchRef = useRef(
		debounce(async (q: string) => {
			if (!q.trim()) {
				setResults(null)
				setIsSearching(false)
				setSearchError(null)
				return
			}
			setSearchError(null)
			try {
				const data = await searchAssistant(q)
				if (q !== queryRef.current) return
				setResults(data)
				const resultCount =
					(data.transactions ?? []).length +
					(data.stores ?? []).length +
					(data.cabinets ?? []).length +
					(data.products ?? []).length +
					(data.promotions ?? []).length +
					(data.marketing ?? []).length +
					(data.learn ?? []).length
				void track('QuickFindSearched', { query: q, resultCount })
			} catch (err) {
				if (q !== queryRef.current) return
				setResults(null)
				setSearchError(err instanceof Error ? err.message : 'Search failed. Try again.')
			} finally {
				if (q === queryRef.current) {
					setIsSearching(false)
				}
			}
		}, 250)
	)

	const handleClearQuery = useCallback(() => {
		setQuery('')
		setResults(null)
		setSearchError(null)
		setIsSearching(false)
		setActiveIndex(-1)
		runSearchRef.current.cancel()
		inputRef.current?.focus()
	}, [])

	useEffect(() => {
		return () => runSearchRef.current.cancel()
	}, [])

	useEffect(() => {
		queryRef.current = query
	}, [query])

	useEffect(() => {
		if (open && user) {
			if (query.trim()) {
				setIsSearching(true)
			}
			runSearchRef.current(query)
		}
		return () => runSearchRef.current.cancel()
	}, [query, open, user])

	useEffect(() => {
		if (open) {
			void loadRecentItems()
		} else {
			setIsRecentItemsLoading(false)
		}
	}, [open, loadRecentItems])

	// Global shortcut
	useEffect(() => {
		if (!user) return
		const handler = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
				event.preventDefault()
				if (open) {
					handleClose()
				} else {
					handleOpen()
				}
			}
		}
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [user, open, handleOpen, handleClose])

	const saveAndClose = useCallback(
		(resultType: string, href: string, label: string, imageUrl?: string, subtitle?: string) => {
			addRecentItem({ label, href, type: resultType, imageUrl, subtitle })
			void track('QuickFindResultClicked', { query: queryRef.current.trim() || undefined, resultType, href, label })
			handleClose()
		},
		[handleClose]
	)

	const goTo = useCallback(
		(href: string, label: string, type?: string) => {
			void track('QuickFindResultClicked', {
				query: queryRef.current.trim() || undefined,
				resultType: type ?? 'navigate',
				href,
				label,
			})
			handleClose()
			navigate(href)
		},
		[handleClose, navigate]
	)

	// Search result sections (entities from backend DB — nav pages handled client-side via fuzzyMatches)
	const sections: SearchResultSection[] = useMemo(() => {
		const transactionItems: SearchResultItem[] = (results?.transactions ?? []).map((transaction) => {
			const href = buildSearchResultHref(transaction)
			return {
				key: `transaction-${transaction.id}`,
				title: transaction.title,
				subtitle: transaction.subtitle,
				href,
				icon: 'transaction' as const,
				resultType: 'transaction',
				resultId: transaction.id,
				action: () => {
					saveAndClose('transaction', href, transaction.title, undefined, transaction.subtitle)
					navigate(href)
				},
			}
		})

		const storeItems: SearchResultItem[] = (results?.stores ?? []).map((store) => {
			const href = buildSearchResultHref(store)
			return {
				key: `store-${store.id}`,
				title: store.title,
				subtitle: store.subtitle,
				href,
				icon: 'store' as const,
				imageUrl: store.imageUrl,
				resultType: 'store',
				resultId: store.id,
				action: () => {
					saveAndClose('store', href, store.title, store.imageUrl, store.subtitle)
					navigate(href)
				},
			}
		})

		const cabinetItems: SearchResultItem[] = (results?.cabinets ?? []).map((cabinet) => {
			const href = buildSearchResultHref(cabinet)
			return {
				key: `cabinet-${cabinet.id}`,
				title: cabinet.title,
				subtitle: cabinet.subtitle,
				href,
				icon: 'cabinet' as const,
				imageUrl: cabinet.imageUrl,
				resultType: 'cabinet',
				resultId: cabinet.id,
				action: () => {
					saveAndClose('cabinet', href, cabinet.title, cabinet.imageUrl, cabinet.subtitle)
					navigate(href)
				},
			}
		})

		const productItems: SearchResultItem[] = (results?.products ?? []).map((product) => {
			const href = buildSearchResultHref(product)
			return {
				key: `product-${product.id}`,
				title: product.title,
				subtitle: product.subtitle,
				href,
				icon: 'product' as const,
				imageUrl: product.imageUrl,
				resultType: 'product',
				resultId: product.id,
				action: () => {
					saveAndClose('product', href, product.title, product.imageUrl, product.subtitle)
					navigate(href)
				},
			}
		})

		const promotionItems: SearchResultItem[] = (results?.promotions ?? []).map((promotion) => {
			const href = buildSearchResultHref(promotion)
			return {
				key: `promotion-${promotion.id}`,
				title: promotion.title,
				subtitle: promotion.subtitle,
				href,
				icon: 'promotion' as const,
				resultType: 'promotion',
				resultId: promotion.id,
				action: () => {
					saveAndClose('promotion', href, promotion.title, undefined, promotion.subtitle)
					navigate(href)
				},
			}
		})

		const marketingItems: SearchResultItem[] = (results?.marketing ?? []).map((asset) => {
			const href = buildSearchResultHref(asset)
			return {
				key: `marketing-${asset.id}`,
				title: asset.title,
				subtitle: asset.subtitle,
				href,
				icon: 'marketing' as const,
				resultType: 'marketing',
				resultId: asset.id,
				action: () => {
					saveAndClose('marketing', href, asset.title, undefined, asset.subtitle)
					navigate(href)
				},
			}
		})

		// Help center ("Learn") articles — external links to learn.micromart.com; open in a new tab.
		const learnItems: SearchResultItem[] = (results?.learn ?? []).map((article) => ({
			key: `learn-${article.id}`,
			title: article.title,
			subtitle: article.subtitle,
			href: article.url,
			icon: 'learn' as const,
			resultType: 'learn',
			resultId: article.id,
			external: true,
			action: () => {
				void track('QuickFindResultClicked', {
					query: queryRef.current.trim() || undefined,
					resultType: 'learn',
					href: article.url,
					label: article.title,
				})
				// Keep the omnibar open — the article opens in a separate tab.
				window.open(article.url, '_blank', 'noopener,noreferrer')
			},
		}))

		const result: SearchResultSection[] = []
		const filterAccessible = (items: SearchResultItem[]) =>
			items.filter((item) => item.href && canAccessOmnibarHref(user, item.href))

		const accessibleStores = filterAccessible(storeItems)
		const accessibleCabinets = filterAccessible(cabinetItems)
		const accessibleProducts = filterAccessible(productItems)
		const accessiblePromotions = filterAccessible(promotionItems)
		const accessibleMarketing = filterAccessible(marketingItems)
		const accessibleTransactions = filterAccessible(transactionItems)

		if (accessibleStores.length) result.push({ label: 'Stores', items: accessibleStores })
		if (accessibleCabinets.length) result.push({ label: 'Cabinets', items: accessibleCabinets })
		if (accessibleProducts.length) result.push({ label: 'Products', items: accessibleProducts })
		if (accessiblePromotions.length) result.push({ label: 'Promotions', items: accessiblePromotions })
		if (accessibleMarketing.length) result.push({ label: 'Marketing', items: accessibleMarketing })
		if (accessibleTransactions.length) result.push({ label: 'Transactions', items: accessibleTransactions })
		if (learnItems.length) result.push({ label: 'Help articles', items: learnItems })
		return result
	}, [results, saveAndClose, navigate, user, handleClose])

	const accessibleQuickActions = useMemo(() => filterCommandItemsForUser(QUICK_ACTIONS, user), [user])
	const accessibleStaticItems = useMemo(() => filterCommandItemsForUser(ALL_STATIC_ITEMS, user), [user])
	const accessibleRecentItems = useMemo(
		() => recentItems.filter((item) => canAccessOmnibarHref(user, item.href)),
		[recentItems, user]
	)

	const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections])

	const trimmedQuery = query.trim()
	const showEmptyState = !trimmedQuery
	const showResults = Boolean(trimmedQuery)

	// Fuzzy-matched static items when user is typing — label/word matches rank above char-sequence fuzzy
	const fuzzyMatches = useMemo(() => {
		if (!trimmedQuery) return []
		const minScore = flatItems.length > 0 ? 0.9 : 0.85
		const bestByHref = new Map<string, { item: CommandItem; score: number }>()
		for (const { item, score } of accessibleStaticItems
			.map((entry) => ({
				item: entry,
				score: scoreCommandItem(trimmedQuery, entry),
			}))
			.filter(({ score }) => score >= minScore)) {
			const existing = bestByHref.get(item.href)
			if (
				!existing ||
				score > existing.score ||
				(score === existing.score && item.description && !existing.item.description)
			) {
				bestByHref.set(item.href, { item, score })
			}
		}
		return [...bestByHref.values()]
			.sort((a, b) => b.score - a.score)
			.slice(0, 5)
			.map(({ item }) => item)
	}, [trimmedQuery, flatItems.length, accessibleStaticItems])

	// Unified flat list for keyboard navigation (works in both empty and search states)
	const allActionableItems: ActionableItem[] = useMemo(() => {
		if (showResults) {
			const items: ActionableItem[] = []
			if (hasAiAccess && trimmedQuery && looksLikeQuestion(trimmedQuery)) {
				items.push({
					key: 'ask-ai',
					label: `Ask AI — "${trimmedQuery}"`,
					section: 'Assistant',
					action: handleAskAi,
				})
			}
			// Entity results first, then nav/page suggestions
			for (const section of sections) {
				for (const item of section.items) {
					items.push({ key: item.key, label: item.title, section: section.label, action: item.action })
				}
			}
			for (const fm of fuzzyMatches) {
				const action = fm.typeformId
					? () => {
							handleClose()
							openTypeform({ formId: fm.typeformId! })
						}
					: fm.href.startsWith('http')
						? () => window.open(fm.href, '_blank')
						: () => goTo(fm.href, fm.label)
				items.push({ key: `fuzzy-${fm.href}`, label: fm.label, section: 'Suggestions', action })
			}

			return items
		}
		const items: ActionableItem[] = []
		for (const a of accessibleQuickActions) {
			items.push({ key: `qa-${a.href}`, label: a.label, section: 'Quick Actions', action: () => goTo(a.href, a.label) })
		}
		for (const r of accessibleRecentItems) {
			items.push({
				key: `recent-${r.href}`,
				label: r.label,
				section: 'Recent',
				action: () => {
					navigate(r.href)
					handleClose()
				},
			})
		}
		return items
	}, [
		showResults,
		sections,
		accessibleRecentItems,
		accessibleQuickActions,
		goTo,
		fuzzyMatches,
		handleClose,
		openTypeform,
		navigate,
		hasAiAccess,
		trimmedQuery,
		handleAskAi,
	])

	// Section boundaries for Tab jumping
	const sectionStartIndices = useMemo(() => {
		const indices: number[] = []
		let lastSection = ''
		for (let i = 0; i < allActionableItems.length; i++) {
			if (allActionableItems[i].section !== lastSection) {
				indices.push(i)
				lastSection = allActionableItems[i].section
			}
		}
		return indices
	}, [allActionableItems])

	const handleSubmit = useCallback(
		(event: FormEvent) => {
			event.preventDefault()
			if (activeIndex >= 0 && allActionableItems[activeIndex]) {
				allActionableItems[activeIndex].action()
			} else if (allActionableItems[0]) {
				allActionableItems[0].action()
			}
		},
		[allActionableItems, activeIndex]
	)

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			const totalItems = allActionableItems.length
			if (event.key === 'ArrowDown') {
				event.preventDefault()
				setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev))
			} else if (event.key === 'ArrowUp') {
				event.preventDefault()
				setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1))
			} else if (event.key === 'Tab' && !event.shiftKey) {
				event.preventDefault()
				const nextStart = sectionStartIndices.find((i) => i > activeIndex)
				setActiveIndex(nextStart ?? sectionStartIndices[0] ?? 0)
			} else if (event.key === 'Tab' && event.shiftKey) {
				event.preventDefault()
				const prevStarts = sectionStartIndices.filter((i) => i < activeIndex)
				setActiveIndex(
					prevStarts.length > 0
						? prevStarts[prevStarts.length - 1]
						: sectionStartIndices[sectionStartIndices.length - 1] ?? 0
				)
			} else if (event.key === 'Escape') {
				handleClose()
			}
		},
		[allActionableItems.length, sectionStartIndices, activeIndex, handleClose]
	)

	const isItemActive = (itemKey: string) => {
		if (activeIndex < 0) return false
		return allActionableItems[activeIndex]?.key === itemKey
	}

	if (!user) return null

	return (
		<>
			<Tooltip title={`Search (${getSearchShortcutHint()})`}>
				<IconButton aria-label="Search" data-test="global-search-trigger" onClick={handleOpen}>
					<SearchIcon />
				</IconButton>
			</Tooltip>

			{/* Search modal */}
			<Dialog
				open={open}
				onClose={handleClose}
				fullScreen={isMobile}
				maxWidth="sm"
				fullWidth
				scroll="paper"
				disableAutoFocus={false}
				disableEnforceFocus
				sx={{
					'& .MuiBackdrop-root': {
						backdropFilter: 'blur(4px)',
					},
					'& .MuiDialog-paper': {
						bgcolor: 'background.paper',
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						...(isMobile
							? {
									m: 0,
									maxHeight: 'none',
									maxWidth: 'none',
								}
							: {
									height: 480,
									m: 4,
									maxHeight: '90vh',
									maxWidth: 600,
								}),
					},
				}}
			>
				<Box
					component="form"
					data-test="global-search-dialog"
					onSubmit={handleSubmit}
					sx={{
						bgcolor: 'background.paper',
						display: 'flex',
						flex: 1,
						flexDirection: 'column',
						minHeight: 0,
						overflow: 'hidden',
					}}
				>
					{/* Search input */}
					<Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 2, gap: 1.5 }}>
						<SearchIcon size={18} color="var(--mui-palette-text-primary)" style={{ flexShrink: 0 }} />
						<InputBase
							autoFocus
							fullWidth
							inputProps={{ 'aria-label': 'Search', 'data-test': 'global-search-input' }}
							inputRef={inputRef}
							onChange={(e) => {
								const val = e.target.value
								setQuery(val)
								setActiveIndex(val.trim() ? 0 : -1)
							}}
							onKeyDown={handleKeyDown}
							placeholder={isMobile ? 'Search products, stores...' : 'Search products, stores, cabinets...'}
							sx={{
								fontSize: { xs: '1rem', sm: '0.9375rem' },
								minHeight: 0,
								'& .MuiInputBase-input': { p: 0 },
							}}
							value={query}
						/>
						{query && !isSearching ? (
							<Button
								aria-label="Clear search"
								data-test="global-search-clear"
								color="inherit"
								onClick={handleClearQuery}
								size="small"
								sx={{
									color: 'text.secondary',
									flexShrink: 0,
									fontSize: '0.8125rem',
									fontWeight: 500,
									minWidth: 0,
									px: 0.5,
									textTransform: 'none',
									'&:hover': { bgcolor: 'transparent', color: 'text.primary' },
								}}
								type="button"
								variant="text"
							>
								Clear
							</Button>
						) : null}
						{isSearching ? <CircularProgress size={18} sx={{ flexShrink: 0 }} /> : null}
						{hasAiAccess && trimmedQuery ? (
							<Tooltip title={`Ask AI about "${trimmedQuery}"`}>
								<Box
									aria-label={`Ask AI about ${trimmedQuery}`}
									data-test="ask-ai-pill"
									component="button"
									onClick={handleAskAi}
									type="button"
									sx={{
										alignItems: 'center',
										bgcolor: 'primary.main',
										border: 'none',
										borderRadius: 1,
										color: 'primary.contrastText',
										cursor: 'pointer',
										display: 'inline-flex',
										flexShrink: 0,
										fontSize: '0.8125rem',
										fontWeight: 600,
										gap: 0.5,
										height: 28,
										lineHeight: 1,
										px: 1.25,
										whiteSpace: 'nowrap',
										'&:hover': {
											bgcolor: 'primary.dark',
										},
										'&:focus-visible': {
											outline: '2px solid',
											outlineColor: 'primary.main',
											outlineOffset: 2,
										},
									}}
								>
									<SparkleIcon size={14} weight="fill" />
									Ask AI
								</Box>
							</Tooltip>
						) : null}
						<IconButton
							aria-label="Close search"
							data-test="global-search-close"
							onClick={handleClose}
							size="small"
							sx={{ flexShrink: 0 }}
						>
							<XIcon size={18} />
						</IconButton>
					</Box>

					<Divider />

					{/* Results area */}
					<Box
						ref={listRef}
						onMouseMove={(e) => {
							const target = (e.target as HTMLElement).closest('[data-nav-index]')
							if (!target || !listRef.current) return
							const items = listRef.current.querySelectorAll('[data-nav-index]')
							const idx = Array.from(items).indexOf(target)
							if (idx >= 0 && idx !== activeIndex) setActiveIndex(idx)
						}}
						sx={{
							bgcolor: 'background.paper',
							flex: 1,
							minHeight: 0,
							overflow: 'auto',
							overscrollBehavior: 'contain',
							pt: 0,
							pb: 1,
							WebkitOverflowScrolling: 'touch',
						}}
					>
						{showEmptyState ? (
							<Box sx={{ px: 2.5, pb: 2 }}>
								<SectionHeader first icon={<LightningIcon color="var(--mui-palette-text-secondary)" size={14} />}>
									Quick actions
								</SectionHeader>
								<Box
									data-test="omnibar-quick-actions"
									sx={{
										display: 'grid',
										gap: 1,
										gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
										mb: accessibleRecentItems.length > 0 ? 1 : 0,
									}}
								>
									{accessibleQuickActions.map((item) => (
										<QuickActionButton
											key={item.label}
											item={item}
											onClick={() => goTo(item.href, item.label)}
											selected={isItemActive(`qa-${item.href}`)}
										/>
									))}
								</Box>

								{accessibleRecentItems.length > 0 ? (
									<>
										<SectionHeader
											action={
												<Typography
													aria-disabled={isRecentItemsLoading}
													aria-label="Clear search history"
													data-test="omnibar-clear-recent"
													color="text.secondary"
													component="button"
													disabled={isRecentItemsLoading}
													fontWeight={500}
													onClick={handleClearHistory}
													type="button"
													variant="caption"
													sx={{
														bgcolor: 'transparent',
														border: 'none',
														cursor: isRecentItemsLoading ? 'default' : 'pointer',
														opacity: isRecentItemsLoading ? 0.5 : 1,
														p: 0,
														'&:hover': {
															color: isRecentItemsLoading ? 'text.secondary' : 'text.primary',
														},
													}}
												>
													Clear recent
												</Typography>
											}
											icon={<HistoryIcon color="var(--mui-palette-text-secondary)" size={14} />}
										>
											Recent
										</SectionHeader>
										<List data-test="omnibar-recent" disablePadding>
											{accessibleRecentItems.map((item) => (
												<ListItemButton
													data-nav-index
													data-test="omnibar-recent-item"
													key={`recent-${item.href}`}
													onClick={() => {
														navigate(item.href)
														handleClose()
													}}
													selected={isItemActive(`recent-${item.href}`)}
													sx={EMPTY_STATE_ROW_SX}
												>
													<ListItemIcon sx={{ minWidth: OMNIBAR_THUMB_PX + 12 }}>
														<OmnibarResultThumbnail icon={item.type} photo={item.imageUrl} showIconBadge />
													</ListItemIcon>
													<ListItemText
														primary={item.label}
														secondary={item.subtitle ?? formatRecentType(item.type)}
														primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }}
														secondaryTypographyProps={{ fontSize: '0.8125rem' }}
													/>
													<IconButton
														aria-label={`Remove ${item.label} from recent searches`}
														size="small"
														onClick={(e) => {
															e.stopPropagation()
															deleteRecentItem(item.href)
															setRecentItems((prev) => prev.filter((r) => r.href !== item.href))
														}}
													>
														<XIcon color="var(--mui-palette-text-primary)" size={14} />
													</IconButton>
												</ListItemButton>
											))}
										</List>
									</>
								) : null}
							</Box>
						) : null}

						{showResults ? (
							<>
								{searchError ? (
									<Typography color="error" sx={{ p: 2 }} variant="body2">
										{searchError}
									</Typography>
								) : flatItems.length === 0 && fuzzyMatches.length === 0 && !isSearching ? (
									<List disablePadding>
										<Box sx={{ px: 2.5, py: 3, textAlign: 'center' }}>
											<Typography color="text.secondary" variant="body2">
												No results for &ldquo;{trimmedQuery}&rdquo;
											</Typography>
										</Box>
									</List>
								) : (
									<List disablePadding>
										{/* API search results */}
										{sections.map((section, sectionIndex) => (
											<Box key={section.label} sx={{ mt: sectionIndex > 0 ? 0.5 : 0 }}>
												<SectionHeader alignWithListItems first={sectionIndex === 0}>
													{section.label}
												</SectionHeader>
												{section.items.map((item) => {
													const isActive = isItemActive(item.key)
													return (
														<ListItemButton
															data-nav-index
															key={item.key}
															onClick={item.action}
															selected={isActive}
															sx={ITEM_SX}
														>
															<ListItemIcon sx={{ minWidth: OMNIBAR_THUMB_PX + 12 }}>
																<OmnibarResultThumbnail icon={item.icon} photo={item.imageUrl} showIconBadge />
															</ListItemIcon>
															<ListItemText
																primary={item.title}
																secondary={item.subtitle}
																primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }}
																secondaryTypographyProps={{ fontSize: '0.75rem' }}
															/>
															<ItemArrow external={item.external} />
														</ListItemButton>
													)
												})}
											</Box>
										))}
										{/* Nav/page suggestions from static items */}
										{fuzzyMatches.length > 0 ? (
											<Box sx={{ mt: sections.length > 0 ? 0.5 : 0 }}>
												<SectionHeader alignWithListItems first={sections.length === 0}>
													Suggestions
												</SectionHeader>
												{fuzzyMatches.map((item) => (
													<ListItemButton
														data-nav-index
														key={`fuzzy-${item.href}`}
														onClick={() =>
															item.href.startsWith('http')
																? window.open(item.href, '_blank')
																: goTo(item.href, item.label)
														}
														selected={isItemActive(`fuzzy-${item.href}`)}
														sx={ITEM_SX}
													>
														<ListItemIcon sx={{ minWidth: OMNIBAR_THUMB_PX + 12 }}>
															{item.accentColor ? (
																<QuickActionIconBadge accentColor={item.accentColor} size={OMNIBAR_THUMB_PX}>
																	{item.icon}
																</QuickActionIconBadge>
															) : (
																<OmnibarIconSlot>{item.icon}</OmnibarIconSlot>
															)}
														</ListItemIcon>
														<ListItemText
															primary={item.label}
															secondary={item.description}
															primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 500 }}
															secondaryTypographyProps={{ fontSize: '0.75rem' }}
														/>
														<ItemArrow />
													</ListItemButton>
												))}
											</Box>
										) : null}
									</List>
								)}
							</>
						) : null}
					</Box>
				</Box>
			</Dialog>
		</>
	)
}
